/**
 * Sticky Add to Cart Handler
 * Handles both regular products and bundle products with specialized behavior
 */
class StickyATCHandler {
  constructor() {
    this.selectors = {
      stickyAtc: '.sticky-atc',
      regularATC: '.atc-button',
      bundleButton: '#bundle-add-to-cart, #refill-add-to-cart',
      bundleStickyButton: '.bundle-sticky-button'
    };
    
    this.state = {
      initialMobileDelay: true // Track if we're in the initial delay period on mobile
    };
    
    // Debug header visibility
    console.log('StickyATCHandler initialized');
    this.debugHeaderVisibility();
    
    this.init();
  }
  
  // Add a debug method to check header visibility
  debugHeaderVisibility() {
    console.log('Debugging header visibility:');
    
    // Check for sticky header element
    const stickyHeader = document.querySelector('sticky-header');
    console.log('Sticky header element exists:', !!stickyHeader);
    
    if (stickyHeader) {
      const computedStyle = window.getComputedStyle(stickyHeader);
      console.log('Sticky header display:', computedStyle.display);
      console.log('Sticky header visibility:', computedStyle.visibility);
      console.log('Sticky header position:', computedStyle.position);
      console.log('Sticky header z-index:', computedStyle.zIndex);
      console.log('Sticky header data-sticky-type:', stickyHeader.getAttribute('data-sticky-type'));
    }
    
    // Check for header wrapper
    const headerWrapper = document.querySelector('.header-wrapper');
    console.log('Header wrapper element exists:', !!headerWrapper);
    
    if (headerWrapper) {
      const computedStyle = window.getComputedStyle(headerWrapper);
      console.log('Header wrapper display:', computedStyle.display);
      console.log('Header wrapper visibility:', computedStyle.visibility);
    }
    
    // Check for header element
    const header = document.querySelector('.header');
    console.log('Header element exists:', !!header);
    
    if (header) {
      const computedStyle = window.getComputedStyle(header);
      console.log('Header display:', computedStyle.display);
      console.log('Header visibility:', computedStyle.visibility);
    }
    
    // Log device info
    console.log('Window width:', window.innerWidth);
    console.log('Is mobile:', window.innerWidth < 750);
    console.log('Current page URL:', window.location.href);
    console.log('Is product page:', window.location.pathname.includes('/products/'));
  }
  
  init() {
    // Set up scroll listener
    document.addEventListener('scroll', this.checkATCScroll.bind(this));
    
    // Add a 2-second delay before showing the button on mobile on initial page load
    if (window.innerWidth < 750) {
      setTimeout(() => {
        this.state.initialMobileDelay = false;
        this.checkATCScroll(); // Check visibility after the delay
      }, 2000);
    } else {
      this.state.initialMobileDelay = false;
    }
    
    try {
      // Initial check
      this.checkATCScroll();
    } catch(e) {
      console.warn('Error during initial ATC scroll check:', e);
    }
    
    // Handle bundle sticky button clicks
    const bundleStickyButton = document.querySelector(this.selectors.bundleStickyButton);
    if (bundleStickyButton) {
      bundleStickyButton.addEventListener('click', this.handleBundleStickyClick.bind(this));
      
      // Update bundle sticky button text to match original bundle button
      this.updateBundleButtonText();
      
      // Set up observers for any changes to the bundle or refill state
      this.observeBundleChanges();
      this.observeRefillChanges(); // Add observer for refill changes too
    }
  }
  
  checkATCScroll() {
    const stickyAtc = document.querySelector(this.selectors.stickyAtc);
    if (!stickyAtc) return;
    
    // Don't proceed if element has been hidden by device-specific CSS
    // This respects the theme settings for device visibility
    const computedStyle = window.getComputedStyle(stickyAtc);
    if (computedStyle.display === 'none') {
      // Debug header visibility on scroll when sticky ATC is hidden
      if (window.innerWidth < 750 && window.location.pathname.includes('/products/')) {
        console.log('Sticky ATC is hidden by CSS, checking header visibility on product page:');
        const stickyHeader = document.querySelector('sticky-header');
        if (stickyHeader) {
          console.log('Sticky header exists on scroll, display:', window.getComputedStyle(stickyHeader).display);
        } else {
          console.log('Sticky header does not exist on scroll');
        }
      }
      return;
    }
    
    // If we're in the initial delay period on mobile, keep the button hidden
    if (this.state.initialMobileDelay && window.innerWidth < 750) {
      stickyAtc.classList.remove('sticky-atc--active');
      return;
    }
    
    // Check for both button types
    const bundleButton = document.querySelector(this.selectors.bundleButton);
    const regularATC = document.querySelector(this.selectors.regularATC);
    
    // Use whichever button exists
    const buttonToCheck = bundleButton || regularATC;
    
    // If no button is found, just make the sticky bar visible
    // This handles cases where the selector might not match
    if (!buttonToCheck) {
      console.log('No ATC button found, showing sticky ATC by default');
      stickyAtc.classList.add('sticky-atc--active');
      return;
    }
    
    // Desktop behavior - show only when scrolled past the button
    if (window.innerWidth >= 750) {
      try {
        // Simple check: are we scrolled past the button?
        const buttonBottom = buttonToCheck.getBoundingClientRect().bottom;
        if (buttonBottom < 0) {
          stickyAtc.classList.add('sticky-atc--active');
        } else {
          stickyAtc.classList.remove('sticky-atc--active');
        }
      } catch (error) {
        // If there's any error, show the sticky bar by default
        console.error('Error checking button position:', error);
        stickyAtc.classList.add('sticky-atc--active');
      }
      return;
    }
    
    // Mobile behavior - show only when scrolled past the button (same as desktop)
    try {
      const buttonBottom = buttonToCheck.getBoundingClientRect().bottom;
      
      // Show sticky add to cart only when we've scrolled past the button
      if (buttonBottom < 0) {
        stickyAtc.classList.add('sticky-atc--active');
      } else {
        stickyAtc.classList.remove('sticky-atc--active');
      }
    } catch (error) {
      // If there's any error, show the sticky bar by default on mobile too
      console.error('Error checking button visibility on mobile:', error);
      stickyAtc.classList.add('sticky-atc--active');
    }
  }
  
  handleBundleStickyClick() {
    // Find and click the original bundle add to cart button
    const bundleAddToCartButton = document.querySelector(this.selectors.bundleButton);
    if (bundleAddToCartButton) {
      console.log('Clicking the original bundle/refill add to cart button:', bundleAddToCartButton);
      bundleAddToCartButton.click();
    } else {
      console.error('Could not find the original bundle/refill add to cart button');
      
      // Fallback: Try to find the refill button by ID specifically
      const refillButton = document.getElementById('refill-add-to-cart');
      if (refillButton) {
        console.log('Found refill button via direct ID, clicking it');
        refillButton.click();
        return;
      }
      
      // Final fallback: Try to find any add to cart button that might be relevant
      const anyAddToCartButton = document.querySelector('[data-add-to-cart], .add-to-cart, button[type="submit"][form^="product-form"]');
      if (anyAddToCartButton) {
        console.log('Using fallback add to cart button:', anyAddToCartButton);
        anyAddToCartButton.click();
      } else {
        console.error('No suitable add to cart button found');
      }
    }
  }
  
  updateBundleButtonText() {
    const bundleStickyButton = document.querySelector(this.selectors.bundleStickyButton);
    
    // Check for either bundle OR refill button
    const bundleButton = document.querySelector('#bundle-add-to-cart');
    const refillButton = document.querySelector('#refill-add-to-cart');
    
    // Use whichever button is found on the page
    const originalButton = bundleButton || refillButton;
    
    if (bundleStickyButton && originalButton) {
      // Get the text from the original button
      const buttonText = originalButton.textContent.trim();
      
      // Update the sticky button text
      const buttonSpan = bundleStickyButton.querySelector('span');
      if (buttonSpan) {
        // Always just use the exact text from the original button
        buttonSpan.textContent = buttonText;
        
        // Log for debugging
        console.log('Updated sticky button text to:', buttonText);
      }
    }
  }
  
  observeBundleChanges() {
    const bundleSelector = document.querySelector('.product-bundle-selector');
    if (!bundleSelector) return;
    
    // Watch for changes to the bundle selector (like selecting colors)
    const observer = new MutationObserver((mutations) => {
      this.updateBundleButtonText();
    });
    
    // Observe changes to the entire bundle selector
    observer.observe(bundleSelector, { 
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ['class']
    });
    
    // Also observe the original bundle button directly
    const bundleButton = document.querySelector(this.selectors.bundleButton);
    if (bundleButton) {
      observer.observe(bundleButton, { 
        childList: true,
        characterData: true,
        subtree: true
      });
    }
    
    // Add click listeners to color selection buttons
    const colorButtons = document.querySelectorAll('.bundle-color-select-button');
    colorButtons.forEach(button => {
      button.addEventListener('click', () => {
        // Wait a moment for the state to update
        setTimeout(() => {
          this.updateBundleButtonText();
        }, 100);
      });
    });
    
    // Listen for drawer close events
    document.addEventListener('click', (event) => {
      if (event.target.matches('[data-close-drawer]') || event.target.closest('[data-close-drawer]')) {
        // Wait for drawer to close and state to update
        setTimeout(() => {
          this.updateBundleButtonText();
        }, 300);
      }
    });
  }
  
  observeRefillChanges() {
    const refillSelector = document.querySelector('.product-refill-selector');
    if (!refillSelector) return;
    
    // Watch for changes to the refill selector (like selecting refill options)
    const observer = new MutationObserver((mutations) => {
      this.updateBundleButtonText();
    });
    
    // Observe changes to the entire refill selector
    observer.observe(refillSelector, { 
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ['class']
    });
    
    // Also observe the original refill button directly
    const refillButton = document.querySelector(this.selectors.bundleButton);
    if (refillButton) {
      observer.observe(refillButton, { 
        childList: true,
        characterData: true,
        subtree: true
      });
    }
  }
}

// Initialize on DOMContentLoaded
document.addEventListener('DOMContentLoaded', () => {
  new StickyATCHandler();
});
